import { useCallback, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";

import { createTicket } from "../../../services/osTicketApi";
import { removeHtmlTagsAndEntities } from "../../../utils";
import useToastPromise from "../../../hooks/useToast";

export function useFeedbackFormState(schema, handleClose) {
  const [textContent, setTextContent] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const methods = useForm({ resolver: yupResolver(schema) });
  const { showToastPromise } = useToastPromise();
  const currentUser = useSelector((state) => state.users.logInUser);

  const { mutate } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(
        createTicket,
        data,
        `Thank you for submitting your request. We will get back to you via email at ${currentUser?.email}.`
      );
    },
    onSuccess: () => {
      handleClose();
    },
  });

  const validateSelectedFiles = useCallback((files) => {
    if (!files.length) {
      return true;
    }

    const totalSize =
      files.reduce((accumulator, file) => accumulator + (file?.size || 0), 0) /
      (1024 * 1024);

    if (totalSize > 10) {
      toast.error("Total file size must not exceed 10MB.");
      return false;
    }

    if (files.length > 5) {
      toast.error("Cannot Upload More than 5 files.");
      return false;
    }

    return true;
  }, []);

  const handleSelectedFiles = useCallback((event) => {
    const filesArray = Object.values(event?.target?.files || {}).filter(
      (file) =>
        !selectedFiles.some((selectedFile) => selectedFile.name === file.name)
    );

    setSelectedFiles((previousFiles) => [...previousFiles, ...filesArray]);
  }, [selectedFiles]);

  const handleDeleteFile = useCallback((filename) => {
    setSelectedFiles((previousFiles) =>
      previousFiles.filter((file) => file.name !== filename)
    );
  }, []);

  const updateMessage = useCallback(
    (message) => {
      methods.setValue("message", message);
      setTextContent(removeHtmlTagsAndEntities(message).trim());
    },
    [methods]
  );

  const onSubmit = useCallback(
    (formData) => {
      if (!validateSelectedFiles(selectedFiles)) {
        return;
      }

      const feedBackFormData = new FormData();
      feedBackFormData.append(
        "message",
        removeHtmlTagsAndEntities(formData?.message || "")
      );
      feedBackFormData.append("subject", formData?.subject);
      feedBackFormData.append("taskboard", window.location.host);

      selectedFiles.forEach((file) => {
        feedBackFormData.append("attachments", file);
      });

      mutate(feedBackFormData);
    },
    [mutate, selectedFiles, validateSelectedFiles]
  );

  return {
    currentUser,
    errors: methods.formState.errors,
    handleDeleteFile,
    handleSelectedFiles,
    methods,
    onSubmit,
    selectedFiles,
    textContent,
    updateMessage,
  };
}
